import Chevron from "components/Chevron";
import { ReactNode, useRef, useState } from "react";
import { prepareForTransition } from "../utils/dom";

interface IFaqCardProps {
  question: ReactNode;
  answer: ReactNode;
  icon?: ReactNode|undefined;
  preExpanded?: boolean;
  style?: React.CSSProperties;
  questionStyle?: React.CSSProperties;
  answerStyle?: React.CSSProperties;
  hideSeparator?: boolean;
  rebrand2025?: boolean;
  withBorder?: boolean;
  withInheritedAnswerTextColor?: boolean;
  questionElement?: keyof JSX.IntrinsicElements
}

const Chevron2025 = () => {
  return (
    <svg width="19" height="11" viewBox="0 0 19 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M14.5688 2.59911C12.8943 4.30105 11.2206 6.00387 9.44874 7.80594C9.33582 7.64407 9.26723 7.50906 9.16649 7.40643C6.9069 5.10704 4.64439 2.81069 2.38102 0.515065C1.75196 -0.12288 0.952382 -0.168339 0.3848 0.395526C-0.156865 0.933735 -0.127707 1.77412 0.478831 2.3913C3.1384 5.09766 5.80137 7.80057 8.46892 10.4989C9.13441 11.1721 9.8793 11.1659 10.5498 10.4871C13.2066 7.79712 15.86 5.10351 18.5101 2.40661C19.1313 1.77452 19.161 0.929726 18.6033 0.379571C18.049 -0.167136 17.2603 -0.121037 16.6379 0.50344C15.945 1.19871 15.2572 1.89927 14.5688 2.59911Z" fill="#4A4A4A" />
    </svg>
  );
}

/** An FAQ card, or really, any element that expands to reveal further text - there is nothing specific to FAQs */
const FaqCard = ({
  question,
  answer,
  icon,
  preExpanded = false,
  style,
  questionStyle,
  answerStyle,
  hideSeparator = false,
  rebrand2025 = false,
  withBorder = false,
  questionElement = 'span',
  withInheritedAnswerTextColor = false,
}: IFaqCardProps) => {
  const [expanded, setExpanded] = useState(preExpanded);
  const isTransitioning = useRef(false);
  const [expandedHeight, setExpandedHeight] = useState<number | undefined>(0);
  const answerContainerRef = useRef<HTMLDivElement>(null);
  const QuestionElement = questionElement;
  return (
    <div className={'faqCard' + (rebrand2025 ? ' rebrand2025' : '') + (withBorder ? ' withBorder' : '') + (withInheritedAnswerTextColor ? ' withInheritedAnswerTextColor' : '') + (expanded ? ' expanded' : '')} data-faq-card-element style={style}>
      <div
        className="question"
        onClick={(e) => {
          if (isTransitioning.current) {
            return;
          }
          isTransitioning.current = true;
          if (!expanded) {
            const answerContainer = e.currentTarget.closest('[data-faq-card-element]')?.querySelector('[data-answer-element]') as HTMLElement|undefined;
            if (answerContainer) {
              // Measure the expanded height of the container, and set that as the height to transition to. Once the
              // transition has ended, reset the height so that the container's height updates if the browser resizes
              const scrollHeight = answerContainer?.scrollHeight;
              const transitionEnd = () => {
                answerContainer.style.height = '';
                answerContainer.removeEventListener('transitionend', transitionEnd);
                isTransitioning.current = false;
              };
              answerContainer.addEventListener('transitionend', transitionEnd);
              setExpandedHeight(scrollHeight);
            } else {
              setExpandedHeight(undefined); 
            }
          } else {
            // Set the height to the scrollHeight, so that the transition can occur when the expanded height is set back to zero
            const scrollHeight = e.currentTarget.closest('[data-faq-card-element]')?.querySelector('[data-answer-element]')?.scrollHeight;
            if (scrollHeight && answerContainerRef.current) {
              const answerContainer = answerContainerRef.current;
              answerContainer.style.height = scrollHeight + "px";
              prepareForTransition(answerContainerRef.current);
              const transitionEnd = () => {
                isTransitioning.current = false;
                answerContainer.removeEventListener('transitionend', transitionEnd);
              };
              answerContainer.addEventListener('transitionend', transitionEnd);
            }
            setExpandedHeight(0);
          }
          setExpanded(!expanded);
        }}
        style={questionStyle}
      >
        {icon && <div className="questionIcon">{icon}</div>}<QuestionElement className="questionText">{question}</QuestionElement>
        {rebrand2025
          ? <div className={'chevron2025 ' + (expanded? 'chevron2025expanded' : '')}><Chevron2025 /></div>
          :
          <Chevron
            direction="down"
            reversed={expanded}
            style={{
              margin: "0 0 0 0.8em",
            }}
          />
        }
      </div>

      {(expanded || rebrand2025) && (
        <div style={{ padding: !rebrand2025 ? "0 2em 2em 2em" : undefined, ...answerStyle }}>
          {!rebrand2025 &&
            <div
              className="separator"
              style={{ width: "100%", margin: "0 0 2em 0" }}
            />
          }
          {rebrand2025
            ? <div ref={answerContainerRef} className="answerContainer" data-answer-element style={{ height: expandedHeight }}>
              <div className="answer">{answer}</div>
            </div>
            : <p className="answer">{answer}</p>
          }
        </div>
      )}

      <style jsx>{`
        .faqCard {
          width: 100%;
          background-color: white;
          border-radius: 3px;
          box-shadow: 0 1px 3px gray;
        }

        .faqCard.rebrand2025 {
          background: rgba(255,255,255,0.8);
          border-radius: 16px;
          box-shadow: none;
        }
        .faqCard.rebrand2025 {
          margin-top: 15px;
        }

        @media (min-width: 768px) {
          .faqCard.rebrand2025 {
            border-radius: 24px;
            margin-top: 25px;
          }
        }
        .faqCard.rebrand2025:first-child {
          margin-top: 0;
        }

        .faqCard.rebrand2025.withBorder {
          border: 2px solid #E1E1E1;
        }

        .question {
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 2em;
          cursor: pointer;
        }
        .faqCard.rebrand2025 .question {
          padding: 15px;
          min-height: 72px;
          font-size: 18px;
          font-weight: 800;
          line-height: 1;
        }
        @media (min-width: 768px) {
          .faqCard.rebrand2025 .question {
            padding: 24px;
            font-size: 24px;
          }
        }

        .questionText {
          flex: 1;
          margin: 0;
          font: inherit;
          text-align: inherit;
          text-transform: inherit;
        }

        .questionIcon {
          width: 22px;
          margin-right: 12px;
        }
        @media (min-width: 768px) {
          width: 30px;
          margin-right: 16px;
        }
        .questionIcon :global(svg) {
          fill: currentColor;
        }

        .chevron2025 {
          margin-left: 15px;
        }
        .chevron2025expanded :global(svg) {
          transform: scaleY(-1);
        }

        .separator {
          ${hideSeparator ? "display: none;" : ""}
        }

        .faqCard.rebrand2025 .answer :global(a) {
          text-decoration: underline;
        }

        .answerContainer {
          transition: height 0.5s;
          overflow: hidden;
        }

        .answer {
          color: #666;
        }
        .withInheritedAnswerTextColor .answer {
          color: inherit;
        }
        .faqCard.rebrand2025 .answer {
          padding: 0 15px 15px 15px;
        }
        @media (min-width: 768px) {
          .faqCard.rebrand2025 .answer {
            padding: 0 24px 24px 24px;
            font-weight: 600;
            font-size: 20px;
            line-height: 1.4;
          }
        }

      `}</style>
    </div>
  );
};

export default FaqCard;
